import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import SegmentedTabs from "@/components/SegmentedTabs";
import { DateRangePicker, DateRangeValue } from "@/components/DateRangePicker";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { RefreshButton } from "@/components/RefreshButton";
import { PHONE_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { fetchAuditLogs } from "../../api";
import { AuditLogEntry, TRAVEL_REQUEST_STATUS_LABEL } from "../../types";
import { formatDateTime } from "@/lib/datetime";

// Local-time day boundaries, same semantics DateRangePicker's own (private)
// startOfDay/endOfDay use - duplicated rather than imported since those
// aren't exported, and this is the only other place that needs them, to
// turn a picked day-range into the since/until ISO bounds the backend's
// filter now takes server-side (list_recent used to be filtered client-side
// over an already-fully-loaded array; now there's only ever one page in
// memory, so the date range has to be a real query filter instead).
function startOfDayIso(d: Date): string {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
}
function endOfDayIso(d: Date): string {
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    23,
    59,
    59,
    999
  ).toISOString();
}

type ActionFilter = "All" | "Roles" | "Policy" | "Requests" | "Approvals";

/** What each action-type tab asks the SERVER for - this used to filter an
 *  already-fully-loaded array client-side; now there's only ever one page
 *  in memory, so the tab has to be a real query filter (db/audit_store.py's
 *  action_prefix/action_in), and changing it re-fetches page 1. */
const ACTION_FILTER_QUERY: Record<
  ActionFilter,
  { actionPrefix?: string; actionIn?: string[] }
> = {
  All: {},
  Roles: { actionPrefix: "role." },
  Policy: { actionPrefix: "policy." },
  // travel_request.submitted / advance.raised - raising something, distinct
  // from a decision being made on it (see Approvals below) - a fixed list,
  // not a shared prefix, since the two don't share one.
  Requests: { actionIn: ["travel_request.submitted", "advance.raised"] },
  Approvals: { actionPrefix: "approval." } // also matches finance_approval./advance_approval. (both contain "approval.")
};

const ACTION_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  "role.assigned": "default",
  "role.revoked": "destructive",
  "policy.updated": "secondary",
  "policy.override_set": "secondary",
  "policy.override_cleared": "secondary",
  "travel_request.submitted": "default",
  "travel_request.report_downloaded": "secondary",
  "travel_request.resubmitted": "default",
  "travel_request.settled": "default",
  "approval.auto_approved": "outline",
  "approval.decided": "outline",
  "approval.review_saved": "outline",
  "approval.resubmit_requested": "destructive",
  "approval.expense_reviewed": "outline",
  "finance_approval.decided": "outline",
  "advance.raised": "default",
  "advance.resubmitted": "default",
  "advance.finance_rejected": "destructive",
  "advance.paid": "default",
  "advance_approval.decided": "outline",
  "advance_approval.review_saved": "outline",
  "advance_approval.resubmit_requested": "destructive"
};

// A few `details` keys are worth a friendlier label than their raw JSON
// key - everything else in `details` still renders, just with its key
// title-cased as a fallback (see DetailPanel below). Covers every key any
// AuditStore.record() call site actually writes (db/roles_store.py,
// db/policy_store.py, db/approvals_store.py, db/advances_store.py,
// requests_api.py) plus "scheme", which no current call site writes but is
// kept so an older entry recorded while it existed still reads properly.
const DETAIL_LABEL: Record<string, string> = {
  decision: "Decision",
  comment: "Comment",
  role: "Role",
  version: "Policy version",
  scheme: "Scheme",
  reason: "Reason",
  stage: "Stage",
  fields: "Fields changed",
  claimMethod: "Claim method",
  travelType: "Trip type",
  bills: "Bills in report",
  receiptsIncluded: "Receipts included",
  receiptsSkipped: "Receipts skipped",
  settledAmount: "Settled amount",
  settledAmounts: "Amounts paid",
  amounts: "Amounts",
  expenseId: "Bill",
  categories: "Expense categories"
};

// Closed set of travel-request/advance status strings any `fromStatus`/
// `toStatus` detail value can hold (see every AuditStore.record() call site
// above) - reuses TRAVEL_REQUEST_STATUS_LABEL's own wording for the statuses
// the two vocabularies share, so the same status never reads two different
// ways in the same app.
const AUDIT_STATUS_LABEL: Record<string, string> = {
  ...TRAVEL_REQUEST_STATUS_LABEL,
  Pending: "Pending",
  Resubmit: "Needs changes",
  Paid: "Paid"
};

const ENTITY_TYPE_LABEL: Record<string, string> = {
  approval: "Travel request",
  advance: "Advance",
  user: "User",
  policy_sheet: "Policy sheet"
};

// "Reason" values are fixed internal codes, not prose - give the one code
// any call site currently writes (approval.auto_approved) a readable form
// instead of showing the raw snake_case string; any other code falls back
// to the raw value rather than guessing at a sentence.
const REASON_LABEL: Record<string, string> = {
  no_reporting_manager: "Requester has no reporting manager"
};

// `fields` is always a list of camelCase document field names (see every
// `fields: sorted(...)` detail above) - split on capitals and lower-case
// the rest so e.g. "estimatedAllowanceAmount" reads as "estimated allowance
// amount" instead of a raw identifier.
function humaniseFieldName(field: string): string {
  return field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
}

const PAGE_SIZE = 20;
// Waits for a pause in typing before the search actually re-fetches - each
// page now costs a real network round-trip (unlike the old client-side
// filter over an already-loaded array), so firing one per keystroke would
// be wasteful and would make results flicker mid-word.
const SEARCH_DEBOUNCE_MS = 350;

export default function AuditLogs() {
  const [logs, setLogs] = React.useState<AuditLogEntry[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState<ActionFilter>("All");
  const [selected, setSelected] = React.useState<AuditLogEntry | null>(null);
  // null = all dates (no date filter).
  const [dateRange, setDateRange] = React.useState<DateRangeValue | null>(null);
  const [page, setPage] = React.useState(0); // 0-based
  // The raw input value (updates every keystroke, for a responsive box) vs.
  // the debounced value actually sent to the server (see SEARCH_DEBOUNCE_MS).
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Paged, not a full-dataset fetch (see api.ts's fetchAuditLogs /
  // admin_api.py's list_audit_logs) - only ever loads the ~20 rows this
  // page actually shows, so this stays cheap regardless of how large the
  // org's audit trail grows. Changing the action tab, date range or search
  // term re-fetches from page 0, same as any other server-filtered list in
  // the app.
  const load = React.useCallback(() => {
    setLoading(true);
    fetchAuditLogs({
      offset: page * PAGE_SIZE,
      limit: PAGE_SIZE,
      ...ACTION_FILTER_QUERY[filter],
      ...(dateRange
        ? { since: startOfDayIso(dateRange.from), until: endOfDayIso(dateRange.to) }
        : {}),
      ...(search ? { actorSearch: search } : {})
    })
      .then(({ logs, total }) => {
        setLogs(logs);
        setTotal(total);
      })
      .finally(() => setLoading(false));
  }, [page, filter, dateRange, search]);

  React.useEffect(() => {
    load();
  }, [load]);

  const changeFilter = (value: ActionFilter) => {
    setFilter(value);
    setPage(0);
  };
  const changeDateRange = (value: DateRangeValue | null) => {
    setDateRange(value);
    setPage(0);
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const emptyMessage = search
    ? "No audit entries match that search."
    : dateRange
      ? "No audit entries for the selected dates."
      : "No audit entries yet.";
  const isPhone = useMediaQuery(PHONE_QUERY);

  const pagination = (
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs text-muted-foreground">
        {total} entr{total === 1 ? "y" : "ies"}
      </p>
      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          Page {page + 1} of {pageCount}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0 || loading}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Previous</span>
          <span className="sr-only sm:hidden">Previous page</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={() => setPage((p) => p + 1)}
          disabled={page + 1 >= pageCount || loading}
        >
          <span className="hidden sm:inline">Next</span>
          <span className="sr-only sm:hidden">Next page</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );

  return (
    <PageContainer fullWidth>
      <PageHeader
        title="Audit Logs"
        description="Role changes, policy updates, and approval decisions."
        titleActions={<RefreshButton onRefresh={load} isLoading={loading} />}
      />

      <div className="space-y-4">
        {/* Phone only: pinned to the top of the page's scroll area (sticky,
           not a layout restructure - the ancestor flex-1 overflow-y-auto
           div in App.tsx's AppShell is already the one scroll container
           every page uses) so the filters and pagination stay in view while
           only the card list beneath scrolls. A solid bg is required since
           sticky content still sits in normal flow - without it, cards
           scrolling underneath would show through - and a bottom border
           marks where the pinned block ends. */}
        <div
          className={cn(
            isPhone &&
              "sticky top-0 z-10 isolate space-y-3 border-b bg-background pb-3 [transform:translateZ(0)]"
          )}
        >
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-auto sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name or email…"
                className="h-9 pl-9"
              />
            </div>
            <SegmentedTabs
              value={filter}
              onChange={changeFilter}
              options={[
                { value: "All", label: "All" },
                { value: "Roles", label: "Roles" },
                { value: "Policy", label: "Policy" },
                { value: "Requests", label: "Requests" },
                { value: "Approvals", label: "Approvals" }
              ]}
            />
            <DateRangePicker
              value={dateRange}
              onChange={changeDateRange}
              allowClear
              align="end"
              className="sm:ml-auto"
            />
          </div>

          {isPhone && pagination}
        </div>

        {isPhone ? (
          <div className="space-y-3">
            {loading ? (
              <p className="rounded-lg border py-10 text-center text-sm text-muted-foreground">
                Loading…
              </p>
            ) : logs.length ? (
              logs.map((log) => (
                <div
                  key={log.id}
                  className="space-y-3 rounded-lg border bg-card p-4 text-sm shadow-sm"
                >
                  <div className="text-[0.9375rem]">
                    <p className="font-medium">
                      {log.actor.displayName || "Unknown user"}
                    </p>
                    {log.actor.email && (
                      <p className="break-words text-xs text-muted-foreground">
                        {log.actor.email}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <dt className="text-xs text-muted-foreground">When</dt>
                      <dd className="mt-0.5 whitespace-nowrap">
                        {formatDateTime(log.createdAt)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Action</dt>
                      <dd className="mt-0.5">
                        <Badge
                          variant={ACTION_VARIANT[log.action] ?? "outline"}
                          className="font-normal"
                        >
                          {log.actionLabel}
                        </Badge>
                      </dd>
                    </div>
                  </div>
                  <div className="-mx-4 flex items-center justify-end border-t px-4 pt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => setSelected(log)}
                    >
                      View details
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </p>
            )}
          </div>
        ) : (
          <ScrollArea className="rounded-md border">
            <Table className="min-w-[40rem]">
              <TableHeader className="bg-muted/50">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-10 text-xs font-medium uppercase tracking-wide">
                    When
                  </TableHead>
                  <TableHead className="h-10 text-xs font-medium uppercase tracking-wide">
                    Actor
                  </TableHead>
                  <TableHead className="h-10 text-xs font-medium uppercase tracking-wide">
                    Action
                  </TableHead>
                  <TableHead className="h-10 text-xs font-medium uppercase tracking-wide" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="h-24 text-center text-sm text-muted-foreground"
                    >
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : logs.length ? (
                  logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="py-3">
                        <span className="whitespace-nowrap text-sm">
                          {formatDateTime(log.createdAt)}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {log.actor.displayName || "Unknown user"}
                          </p>
                          {log.actor.email && (
                            <p className="truncate text-xs text-muted-foreground">
                              {log.actor.email}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-3">
                        <Badge
                          variant={ACTION_VARIANT[log.action] ?? "outline"}
                          className="font-normal"
                        >
                          {log.actionLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8"
                          onClick={() => setSelected(log)}
                        >
                          View details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="h-24 text-center text-sm text-muted-foreground"
                    >
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        )}

        {!isPhone && pagination}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          {selected && <DetailPanel entry={selected} />}
        </SheetContent>
      </Sheet>
    </PageContainer>
  );
}

/** Renders one `details` value in a human-readable form, keyed by what the
 *  key itself means rather than the value's raw JS type - a `fields` array
 *  is a list of document field names, a `fromStatus`/`toStatus` string is a
 *  status code, a `reason` is a fixed internal code, everything else not
 *  specifically known just falls back to its plain string/JSON form. */
function formatDetailValue(key: string, value: unknown): React.ReactNode {
  if (key === "fields" && Array.isArray(value)) {
    return value.length
      ? value.map((f) => humaniseFieldName(String(f))).join(", ")
      : "-";
  }
  if (key === "categories" && Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }
  if ((key === "fromStatus" || key === "toStatus") && typeof value === "string") {
    return AUDIT_STATUS_LABEL[value] || value;
  }
  if (key === "reason" && typeof value === "string") {
    return REASON_LABEL[value] || value;
  }
  if (typeof value === "string") return value || "-";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function DetailPanel({ entry }: { entry: AuditLogEntry }) {
  const details = entry.details || {};
  // fromStatus/toStatus are always recorded as a pair (see every
  // AuditStore.record() call site that writes either one) - shown as one
  // "Submitted -> Pending Finance" row instead of two separate ones.
  const fromStatus = details["fromStatus"];
  const toStatus = details["toStatus"];
  const hasTransition =
    typeof fromStatus === "string" && typeof toStatus === "string";
  const detailEntries = Object.entries(details).filter(
    ([k, v]) =>
      v !== null &&
      v !== undefined &&
      !(hasTransition && (k === "fromStatus" || k === "toStatus"))
  );

  return (
    <>
      <SheetHeader>
        <SheetTitle>Audit entry details</SheetTitle>
      </SheetHeader>
      <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        <Row label="When" value={formatDateTime(entry.createdAt)} />
        <Row label="Action" value={entry.actionLabel} />
        <Row label="Actor" value={entry.actor.displayName || "Unknown user"} />
        <Row label="Actor email" value={entry.actor.email || "-"} />
        <Row label="Entity" value={entry.entitySummary} />
        <Row
          label="Entity type"
          value={ENTITY_TYPE_LABEL[entry.entityType] || entry.entityType}
        />
        {hasTransition && (
          <div className="sm:col-span-2">
            <Row
              label="Status change"
              value={`${AUDIT_STATUS_LABEL[fromStatus as string] || fromStatus || "-"} → ${
                AUDIT_STATUS_LABEL[toStatus as string] || toStatus || "-"
              }`}
            />
          </div>
        )}
        {detailEntries.map(([key, value]) => (
          <Row
            key={key}
            label={
              DETAIL_LABEL[key] ||
              key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (c) => c.toUpperCase())
            }
            value={formatDetailValue(key, value)}
          />
        ))}
        <details className="sm:col-span-2">
          <summary className="cursor-pointer select-none text-xs text-muted-foreground hover:text-foreground">
            Technical details
          </summary>
          <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <Row
              label="Full actor id"
              value={<span className="font-mono text-xs">{entry.actorId}</span>}
            />
            <Row
              label="Full entity id"
              value={<span className="font-mono text-xs">{entry.entityId}</span>}
            />
          </div>
        </details>
      </dl>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words font-medium">{value}</dd>
    </div>
  );
}
